import React from "react";
import * as yup from "yup";
import { ModalForm, useModalDispatch } from "collections/Modal";
import i18n, { useTranslation } from "utilities/i18n";
import { Form, Input, StandardFormSelect } from "collections/Form";
import { CancelButton, OptionType, SubmitButton } from "elements";
import { CallType, useStartCallMutation } from "generated/graphql";
import { DISMISS, useNotification } from "collections";

type DialType = CallType.Sip | CallType.Tel | CallType.H323;

const dialTypeOptions: Record<DialType, OptionType> = {
  [CallType.Sip]: {
    value: CallType.Sip,
    text: "SIP"
  },
  [CallType.Tel]: {
    value: CallType.Tel,
    text: "TEL"
  },
  [CallType.H323]: {
    value: CallType.H323,
    text: "H.323"
  }
};

export const DialOutSchema = yup.object().shape({
  callTo: yup
    .string()
    .label(i18n.t("CALL_TO"))
    .required(),
  dialType: yup
    .mixed<DialType>()
    .oneOf(
      Object.values(dialTypeOptions).map(option => option.value as DialType)
    )
    .label(i18n.t("DIAL_TYPE"))
    .required(),
  lineNumber: yup
    .number()
    .label(i18n.t("LINE_NUMBER"))
    .required()
    .positive()
});

type DialOutFormValues = yup.InferType<typeof DialOutSchema>;

export const DialOutModal: React.FC<{
  deviceId: string;
}> = ({ deviceId }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const closeModal = useModalDispatch();
  const [, startCall] = useStartCallMutation();

  const dialOut = async (values: DialOutFormValues) => {
    const { data } = await startCall({
      deviceId,
      dialString: values.callTo,
      options: {
        type: values.dialType,
        line: values.lineNumber.toString()
      }
    });

    if (data?.startCall.success) {
      addNotification({
        id: "dialOutSuccess",
        type: "success",
        persistThroughNav: true,
        dismiss: DISMISS.DEFAULT,
        message: t("SUCCESSFULLY_STARTED_CALL")
      });
      closeModal();
    } else {
      addNotification({
        id: "dialOutError",
        type: "error",
        persistThroughNav: true,
        dismiss: DISMISS.DEFAULT,
        message: t("ERROR_STARTING_CALL")
      });
    }
  };

  return (
    <ModalForm<DialOutFormValues>
      name="DialOut"
      title={t("DIAL_OUT")}
      validationSchema={DialOutSchema}
      initialValues={{
        callTo: "",
        dialType: CallType.Sip,
        lineNumber: 1
      }}
      onSubmit={dialOut}
      validateOnMount
    >
      {({ isValid, isSubmitting }) => {
        return (
          <Form>
            <Input name="callTo" type="text" label={t("CALL_TO")} />
            <StandardFormSelect
              name="dialType"
              label={t("DIAL_TYPE")}
              options={Object.values(dialTypeOptions)}
            />
            <Input name="lineNumber" type="number" label={t("LINE_NUMBER")} />
            <div className="btn-container">
              <CancelButton onClick={closeModal} disabled={isSubmitting} />
              <SubmitButton
                isSubmitting={isSubmitting}
                disabled={!isValid}
                labelKey="DIAL_OUT"
                submittingLabelKey="DIALING"
              />
            </div>
          </Form>
        );
      }}
    </ModalForm>
  );
};
